import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  DollarSign,
  User,
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Search, 
  Filter, 
  Download,
  Eye,
  Edit,
  RefreshCw,
  Phone,
  Mail,
  FileText,
  Users,
  TrendingUp,
  Activity
} from 'lucide-react';
import AdminLayout from '../common/AdminLayout';

interface Booking {
  id: number;
  bookingReference: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  workerName?: string;
  workerEmail?: string;
  workerPhone?: string;
  categoryName: string;
  skillName?: string;
  title: string;
  description: string;
  locationAddress: string;
  parish: string;
  scheduledDate: string;
  scheduledTime: string;
  estimatedDuration?: number;
  budgetMin?: number;
  budgetMax?: number;
  actualAmount?: number;
  status: 'pending' | 'assigned' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  paymentStatus: 'pending' | 'paid' | 'refunded';
  completionDate?: string;
  cancellationReason?: string;
  clientNotes?: string;
  workerNotes?: string;
  createdAt: string;
  updatedAt: string;
}

const BookingManagement: React.FC = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedBookings, setSelectedBookings] = useState<number[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'view' | 'edit' | 'assign' | 'cancel'>('view');
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);

  // Mock data for demonstration
  useEffect(() => {
    setTimeout(() => {
      setBookings([
        {
          id: 1,
          bookingReference: 'BK-2024-001',
          clientName: 'John Smith',
          clientEmail: 'john.smith@email.com',
          clientPhone: '+1234567890',
          workerName: 'Mike Johnson',
          workerEmail: 'mike.j@email.com',
          workerPhone: '+1234567891',
          categoryName: 'Home Maintenance',
          skillName: 'Plumbing',
          title: 'Kitchen Sink Repair',
          description: 'Kitchen sink is leaking and needs urgent repair. Water damage prevention required.',
          locationAddress: '123 Main St, Downtown',
          parish: 'Kingston',
          scheduledDate: '2024-01-20',
          scheduledTime: '14:00',
          estimatedDuration: 120,
          budgetMin: 50,
          budgetMax: 150,
          actualAmount: 125,
          status: 'completed',
          priority: 'high',
          paymentStatus: 'paid',
          completionDate: '2024-01-20T16:30:00',
          clientNotes: 'Please call before arriving',
          workerNotes: 'Replaced faucet and gaskets',
          createdAt: '2024-01-18T10:30:00',
          updatedAt: '2024-01-20T16:30:00'
        },
        {
          id: 2,
          bookingReference: 'BK-2024-002',
          clientName: 'Sarah Wilson',
          clientEmail: 'sarah.w@email.com',
          clientPhone: '+1234567892',
          categoryName: 'Cleaning Services',
          skillName: 'Deep Cleaning',
          title: 'Post-Renovation Cleaning',
          description: 'Deep cleaning required after home renovation. Multiple rooms need attention.',
          locationAddress: '456 Oak Ave, Uptown',
          parish: 'St. Andrew',
          scheduledDate: '2024-01-22',
          scheduledTime: '09:00',
          estimatedDuration: 240,
          budgetMin: 100,
          budgetMax: 200,
          status: 'pending',
          priority: 'medium',
          paymentStatus: 'pending',
          clientNotes: 'Keys under mat, please text when arriving',
          createdAt: '2024-01-19T14:15:00',
          updatedAt: '2024-01-19T14:15:00'
        },
        {
          id: 3,
          bookingReference: 'BK-2024-003',
          clientName: 'David Brown',
          clientEmail: 'david.b@email.com',
          clientPhone: '+1234567893',
          workerName: 'Lisa Davis',
          workerEmail: 'lisa.d@email.com',
          workerPhone: '+1234567894',
          categoryName: 'Electrical Services',
          skillName: 'Electrical Repair',
          title: 'Outlet Installation',
          description: 'Need 3 new outlets installed in the living room and bedroom.',
          locationAddress: '789 Pine St, Midtown',
          parish: 'Spanish Town',
          scheduledDate: '2024-01-21',
          scheduledTime: '11:00',
          estimatedDuration: 180,
          budgetMin: 80,
          budgetMax: 180,
          status: 'in_progress',
          priority: 'low',
          paymentStatus: 'pending',
          clientNotes: 'Home office setup, please be careful with equipment',
          workerNotes: 'Materials purchased, installation in progress',
          createdAt: '2024-01-17T16:45:00',
          updatedAt: '2024-01-21T11:30:00'
        },
        {
          id: 4,
          bookingReference: 'BK-2024-004',
          clientName: 'Emma Taylor',
          clientEmail: 'emma.t@email.com',
          clientPhone: '+1234567895',
          categoryName: 'Gardening',
          skillName: 'Lawn Care',
          title: 'Garden Maintenance',
          description: 'Weekly garden maintenance including lawn mowing, hedge trimming, and flower bed care.',
          locationAddress: '321 Garden Rd, Suburbia',
          parish: 'St. Catherine',
          scheduledDate: '2024-01-25',
          scheduledTime: '08:00',
          estimatedDuration: 180,
          budgetMin: 60,
          budgetMax: 120,
          status: 'cancelled',
          priority: 'low',
          paymentStatus: 'refunded',
          cancellationReason: 'Client rescheduled to next month',
          clientNotes: 'Please avoid the rose bushes in the back',
          createdAt: '2024-01-16T09:20:00',
          updatedAt: '2024-01-19T13:45:00'
        }
      ]);
      setLoading(false);
    }, 1000);
  }, []);

  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = 
      booking.bookingReference.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.categoryName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || booking.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || booking.priority === priorityFilter;
    
    let matchesDate = true;
    if (dateFilter !== 'all') {
      const bookingDate = new Date(booking.scheduledDate);
      const today = new Date();
      switch (dateFilter) {
        case 'today':
          matchesDate = bookingDate.toDateString() === today.toDateString();
          break;
        case 'tomorrow':
          const tomorrow = new Date(today);
          tomorrow.setDate(today.getDate() + 1);
          matchesDate = bookingDate.toDateString() === tomorrow.toDateString();
          break;
        case 'this_week':
          const weekStart = new Date(today);
          weekStart.setDate(today.getDate() - today.getDay());
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6);
          matchesDate = bookingDate >= weekStart && bookingDate <= weekEnd;
          break;
      }
    }
    
    return matchesSearch && matchesStatus && matchesPriority && matchesDate;
  });

  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);
  const currentBookings = filteredBookings.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSelectBooking = (bookingId: number) => {
    setSelectedBookings(prev => 
      prev.includes(bookingId) 
        ? prev.filter(id => id !== bookingId)
        : [...prev, bookingId]
    );
  };

  const handleSelectAll = () => {
    if (selectedBookings.length === currentBookings.length) {
      setSelectedBookings([]);
    } else {
      setSelectedBookings(currentBookings.map(b => b.id));
    }
  };

  const handleAction = (booking: Booking, action: 'view' | 'edit' | 'assign' | 'cancel') => {
    setSelectedBooking(booking);
    setModalType(action);
    setShowModal(true);
  };

  const handleStatusUpdate = (bookingId: number, newStatus: string) => {
    setBookings(prev => prev.map(booking => 
      booking.id === bookingId 
        ? { ...booking, status: newStatus as any, updatedAt: new Date().toISOString() }
        : booking
    ));
  };

  const exportBookings = () => {
    const csvContent = [
      ['Reference', 'Client', 'Worker', 'Service', 'Date', 'Status', 'Priority', 'Amount'].join(','),
      ...filteredBookings.map(booking => [
        booking.bookingReference,
        booking.clientName,
        booking.workerName || 'Unassigned',
        booking.title,
        booking.scheduledDate,
        booking.status,
        booking.priority,
        booking.actualAmount || booking.budgetMax || 0
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bookings.csv';
    a.click();
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      pending: 'badge-warning',
      assigned: 'badge-info',
      in_progress: 'badge-info',
      completed: 'badge-success',
      cancelled: 'badge-danger'
    };
    return `badge ${colors[status as keyof typeof colors] || 'badge-secondary'}`;
  };

  const getPriorityBadge = (priority: string) => {
    const colors = {
      low: 'badge-info',
      medium: 'badge-warning',
      high: 'badge-danger',
      urgent: 'badge-danger'
    };
    return `badge ${colors[priority as keyof typeof colors] || 'badge-secondary'}`;
  };

  const getPaymentBadge = (paymentStatus: string) => {
    const colors = {
      pending: 'badge-warning',
      paid: 'badge-success',
      refunded: 'badge-danger'
    };
    return `badge ${colors[paymentStatus as keyof typeof colors] || 'badge-secondary'}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <AdminLayout title="Booking & Job Management" subtitle="Manage and monitor platform bookings">
        <div className="loading-spinner" />
      </AdminLayout>
    );
  }

  // Calculate statistics
  const stats = {
    total: bookings.length,
    pending: bookings.filter(b => b.status === 'pending').length,
    inProgress: bookings.filter(b => b.status === 'in_progress').length,
    completed: bookings.filter(b => b.status === 'completed').length,
    cancelled: bookings.filter(b => b.status === 'cancelled').length,
    revenue: bookings.filter(b => b.actualAmount).reduce((sum, b) => sum + (b.actualAmount || 0), 0)
  };

  return (
    <AdminLayout title="Booking & Job Management" subtitle="Manage and monitor platform bookings">
      <div className="fade-in">
        {/* Header Actions */}
        <div className="admin-card" style={{ marginBottom: '25px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
              {/* Search */}
              <div style={{ position: 'relative', minWidth: '300px' }}>
                <input
                  type="text"
                  placeholder="Search bookings..."
                  className="form-control"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingLeft: '40px' }}
                />
                <Search size={18} style={{ 
                  position: 'absolute', 
                  left: '12px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: 'var(--text-dark-gray)'
                }} />
              </div>

              {/* Filters */}
              <select 
                className="form-control" 
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ minWidth: '120px' }}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="assigned">Assigned</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>

              <select 
                className="form-control" 
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                style={{ minWidth: '120px' }}
              >
                <option value="all">All Priority</option>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>

              <select 
                className="form-control" 
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                style={{ minWidth: '150px' }}
              >
                <option value="all">All Dates</option>
                <option value="today">Today</option>
                <option value="tomorrow">Tomorrow</option>
                <option value="this_week">This Week</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="btn-secondary" onClick={exportBookings}>
                <Download size={18} />
                Export
              </button>
              <button className="btn-primary">
                <RefreshCw size={18} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="stat-cards" style={{ marginBottom: '25px' }}>
          <div className="stat-card">
            <div className="stat-icon">
              <Calendar size={24} />
            </div>
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Total Bookings</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <Clock size={24} />
            </div>
            <div className="stat-value">{stats.pending}</div>
            <div className="stat-label">Pending Assignment</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <Activity size={24} />
            </div>
            <div className="stat-value">{stats.inProgress}</div>
            <div className="stat-label">In Progress</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <CheckCircle size={24} />
            </div>
            <div className="stat-value">{stats.completed}</div>
            <div className="stat-label">Completed</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <DollarSign size={24} />
            </div>
            <div className="stat-value">${stats.revenue.toLocaleString()}</div>
            <div className="stat-label">Total Revenue</div>
          </div>
        </div>

        {/* Bookings Table */}
        <div className="admin-card">
          <div className="card-header">
            <div className="card-title">Bookings & Jobs</div>
            <div style={{ fontSize: '14px', color: 'var(--text-medium-gray)' }}>
              Showing {currentBookings.length} of {filteredBookings.length} bookings
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>
                    <input
                      type="checkbox"
                      checked={selectedBookings.length === currentBookings.length && currentBookings.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Reference</th>
                  <th>Client</th>
                  <th>Service</th>
                  <th>Worker</th>
                  <th>Schedule</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Payment</th>
                  <th>Amount</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentBookings.map(booking => (
                  <tr key={booking.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedBookings.includes(booking.id)}
                        onChange={() => handleSelectBooking(booking.id)}
                      />
                    </td>
                    <td>
                      <div>
                        <div style={{ fontWeight: '500', marginBottom: '2px' }}>
                          {booking.bookingReference}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)' }}>
                          {formatDate(booking.createdAt)}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div style={{ fontWeight: '500', marginBottom: '2px' }}>
                          {booking.clientName}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)' }}>
                          {booking.clientPhone}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div style={{ fontWeight: '500', marginBottom: '2px' }}>
                          {booking.title}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)' }}>
                          {booking.categoryName}
                        </div>
                      </div>
                    </td>
                    <td>
                      {booking.workerName ? (
                        <div>
                          <div style={{ fontWeight: '500', marginBottom: '2px' }}>
                            {booking.workerName}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)' }}>
                            {booking.workerPhone}
                          </div>
                        </div>
                      ) : (
                        <span className="badge badge-warning">Unassigned</span>
                      )}
                    </td>
                    <td>
                      <div>
                        <div style={{ fontWeight: '500', marginBottom: '2px' }}>
                          {formatDate(booking.scheduledDate)}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)' }}>
                          {formatTime(booking.scheduledTime)}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div style={{ fontSize: '14px', marginBottom: '2px' }}>
                          {booking.parish}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)' }}>
                          {booking.locationAddress.substring(0, 30)}...
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={getStatusBadge(booking.status)}>
                        {booking.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td>
                      <span className={getPriorityBadge(booking.priority)}>
                        {booking.priority}
                      </span>
                    </td>
                    <td>
                      <span className={getPaymentBadge(booking.paymentStatus)}>
                        {booking.paymentStatus}
                      </span>
                    </td>
                    <td>
                      <div>
                        {booking.actualAmount ? (
                          <div style={{ fontWeight: '500' }}>
                            ${booking.actualAmount}
                          </div>
                        ) : (
                          <div style={{ fontSize: '12px', color: 'var(--text-medium-gray)' }}>
                            ${booking.budgetMin}-${booking.budgetMax}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button 
                          className="btn-secondary" 
                          style={{ padding: '6px', minWidth: 'auto' }}
                          onClick={() => handleAction(booking, 'view')}
                          title="View Details"
                        >
                          <Eye size={14} />
                        </button>
                        <button 
                          className="btn-secondary" 
                          style={{ padding: '6px', minWidth: 'auto' }}
                          onClick={() => handleAction(booking, 'edit')}
                          title="Edit Booking"
                        >
                          <Edit size={14} />
                        </button>
                        {booking.status === 'pending' && (
                          <button 
                            className="btn-primary" 
                            style={{ padding: '6px', minWidth: 'auto' }}
                            onClick={() => handleAction(booking, 'assign')}
                            title="Assign Worker"
                          >
                            <Users size={14} />
                          </button>
                        )}
                        <button 
                          className="btn-danger" 
                          style={{ padding: '6px', minWidth: 'auto' }}
                          onClick={() => handleAction(booking, 'cancel')}
                          title="Cancel Booking"
                        >
                          <XCircle size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginTop: '20px',
            padding: '20px 0'
          }}>
            <div style={{ fontSize: '14px', color: 'var(--text-medium-gray)' }}>
              Page {currentPage} of {totalPages}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                className="btn-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              >
                Previous
              </button>
              <button 
                className="btn-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default BookingManagement; 